import React, { SyntheticEvent } from "react";
import { isEmpty } from "lodash";
import { makeStyles } from "@material-ui/core";
import {
  Business,
  CardTravel,
  Commute,
  FileCopy,
  Hotel,
  People,
  AssignmentTurnedIn
} from "@material-ui/icons";
import { NodeType, NodeRef } from "@graphql";
import { ThemeType } from "@theme";
import { store } from "@store";
import { PlainLink } from "@components";
import { AccommodationRequestViewLocation } from "@bookings/BookingHospitality/locations/AccommodationRequestViewLocation";
import { TravelRequestViewLocation } from "@bookings/BookingHospitality/locations/TravelRequestViewLocation";
import { ConsultantProfileLocation } from "@navigation/locations/consultant-profile.location";
import { CustomerProfileLocation } from "@navigation/locations/CustomerProfileLocation";
import { BookingProfileLocation } from "@navigation/locations/BookingProfileLocation";
import { CustomerContractProfileLocation } from "@contracts/locations/CustomerContractProfileLocation";

type Props = {
  items: NodeRef[];
  style?: React.CSSProperties;
  inline?: boolean;
  onClick?: (e: SyntheticEvent<any>) => void;
};

export function NodeRefList(props: Props) {
  const classes = useStyles({});

  if (isEmpty(props.items)) {
    return null;
  }

  return (
    <div className={classes.root} style={props.style}>
      {props.items.map((item) => {
        const params = nodeTypeParams[item.NodeType];

        if (!params) {
          return null;
        }

        const iconCmp = params.icon;
        const icon = iconCmp
          ? React.createElement(iconCmp, { className: classes.icon })
          : null;

        return (
          <div key={item.NodeID} className={classes.item}>
            <PlainLink inline onClick={() => params.onClick(item)}>
              {icon}&nbsp;{item.Name}
            </PlainLink>
          </div>
        );
      })}
    </div>
  );
}

export function NodeRefListInline(props: Props) {
  const classes = useStyles({});

  if (isEmpty(props.items)) {
    return null;
  }

  const items = props.items.map((item) => {
    const params = nodeTypeParams[item.NodeType];

    if (!params) {
      return null;
    }

    const iconCmp = params.icon;
    const icon = iconCmp
      ? React.createElement(iconCmp, { className: classes.iconInline })
      : null;

    return (
      <PlainLink
        key={item.NodeID}
        inline
        onClick={(e) => {
          props.onClick?.(e);
          params.onClick(item);
        }}
        style={{ alignItems: "baseline", marginRight: 4 }}
      >
        {icon}&nbsp;{item.Name}
      </PlainLink>
    );
  });

  return <>{items}</>;
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginRight(4),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center"
  },

  item: {
    display: "inline-flex",
    alignItems: "center",

    "&:not(:last-child):after": {
      fontFamily: theme.typography.fontFamily,
      fontSize: 12,
      color: theme.palette.secondary.main,
      content: "','"
    }
  },

  icon: {
    fontSize: 16,
    marginRight: 2
  },

  iconInline: {
    fontSize: 16,
    marginRight: 2,
    position: "relative",
    top: 2
  }
}));

const nodeTypeParams: PartialRecord<
  NodeType,
  {
    icon: React.ComponentType<any>;
    onClick: (ref: NodeRef) => void;
  }
> = {
  [NodeType.AccommodationRequest]: {
    icon: Hotel,
    onClick: (ref) => {
      store().navigation.open(
        "noderef_viewer",
        new AccommodationRequestViewLocation({ requestId: ref.NodeID })
      );
    }
  },

  [NodeType.TravelRequest]: {
    icon: Commute,
    onClick: (ref) => {
      store().navigation.open(
        "noderef_viewer",
        new TravelRequestViewLocation({
          requestId: ref.NodeID
        })
      );
    }
  },

  [NodeType.Consultant]: {
    icon: People,
    onClick: (ref) => {
      store().navigation.open(
        "noderef_viewer",
        new ConsultantProfileLocation(ref.NodeID)
      );
    }
  },

  [NodeType.Customer]: {
    icon: Business,
    onClick: (ref) => {
      store().navigation.open("noderef_viewer", new CustomerProfileLocation(ref.NodeID));
    }
  },

  [NodeType.Booking]: {
    icon: CardTravel,
    onClick: (ref) => {
      store().navigation.open("noderef_viewer", new BookingProfileLocation(ref.NodeID));
    }
  },

  [NodeType.File]: { icon: FileCopy, onClick: null },

  [NodeType.CustomerContract]: {
    icon: AssignmentTurnedIn,
    onClick: (ref) => {
      store().navigation.open(
        "noderef_viewer",
        new CustomerContractProfileLocation(ref.NodeID)
      );
    }
  }
};
